import React, { useCallback, useEffect, useState, useRef } from 'react';
import useFetch from '../../hooks/useFetch';
import { useSelector } from 'react-redux';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFacebook, faInstagram, faLinkedin, faTwitter } from '@fortawesome/free-brands-svg-icons';
import logo from '../../assets/logo.png';
import "../../styles/MyProfile.css";
import { faBriefcaseClock, faVolumeHigh, faDownload, faStop } from '@fortawesome/free-solid-svg-icons';
import QRCode from 'react-qr-code';
import html2canvas from 'html2canvas';

const PDTalkingCard = () => {
  const authState = useSelector(state => state.authReducer);
  const [fetchData] = useFetch();
  const userId = authState.user._id;

  const [profileData, setProfileData] = useState({
    userId: authState.user._id,
    name: authState.user.name,
    gender: authState.user.gender,
    dob: '',
    email: authState.user.email,
    mobile: authState.user.mobile,
    city: authState.user.city,
    country: authState.user.country,
    addl: authState.user.remarks,
    institution: '',
    socialLinks: '',
    personalPageLink: '',
    think5whysCode: authState.user.think5whyscode,
    image: '',
  });

  const [profileExists, setProfileExists] = useState(false);
  const [profileId, setProfileId] = useState('');
  const [ttsLang, setTtsLang] = useState("en-IN");  
  const [isSpeaking, setIsSpeaking] = useState(false);

  const cardRef = useRef(null); // Ref for capturing card for download

  useEffect(() => {
    fetchUserProfile(userId);
  }, [userId]);

  const fetchUserProfile = useCallback(() => {
    const config = {
      url: "/myprofile/byid",
      method: 'post',
      data: { userId: userId },
      headers: { Authorization: authState.token },
    };

    fetchData(config)
      .then(response => {
        if (response && response.status) {
          setProfileExists(true);
          const { profile } = response;
          setProfileId(profile._id);
          setProfileData({
            userId: profile.userId,
            name: profile.name,
            gender: profile.gender,
            dob: profile.dob,
            email: profile.email,
            mobile: profile.mobile,
            city: profile.city,
            country: profile.country,
            institution: profile.institution,
            addl: authState.user.remarks,
            socialLinks: profile.socialLinks,
            personalPageLink: profile.personalPageLink,
            think5whysCode: profile.think5whysCode,
            image: profile.image
          });
        }
      })
      .catch(() => {});
  }, [userId, fetchData, authState.token]);

  const speakCard = () => {
      const msg = new SpeechSynthesisUtterance();

      msg.lang = ttsLang;
      msg.text =
        `Introducing the digital identity card of ${profileData.name}!

        Here are the key details.

        Think 5 Whys Code: ${profileData.think5whysCode}.
        Email: ${profileData.email}.
        Mobile: ${profileData.mobile}.

        Location details:
        ${profileData.city}, ${profileData.country}.

        This profile belongs to ${profileData.name}, part of the Purpule Door Behavioral Operating System in Think 5 Whys ecosystem.`;

      // IMPORTANT: assign handlers BEFORE speak
      msg.onend = () => setIsSpeaking(false);
      msg.onerror = () => setIsSpeaking(false);

      speechSynthesis.cancel();
      speechSynthesis.speak(msg);

      setIsSpeaking(true);
  };

  const stopSpeaking = () => {
      speechSynthesis.cancel();
      setIsSpeaking(false);
  };

  // --------------------- DOWNLOAD FUNCTION ---------------------
  const downloadCard = () => {
    if (!cardRef.current) return;

    // Hide buttons before capture
    const buttons = cardRef.current.querySelectorAll('.hide-for-download');
    buttons.forEach(btn => btn.style.display = 'none');

    html2canvas(cardRef.current, { scale: 2 }).then(canvas => {
      const link = document.createElement('a');
      link.download = `${profileData.name}_DigitalCard.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();

      // Restore buttons
      buttons.forEach(btn => btn.style.display = '');
    });
  };

  return (
    <div className="flex flex-col justify-center items-center space-y-6 bg-gray-900 p-4 rounded-xl text-white border-t-2 border-cyan-200 mt-2">

      {/* CARD UI with QR included */}
      <div
        ref={cardRef}
        className="bg-gradient-to-r from-gray-200 to-gray-400 border border-gray-600 rounded-lg shadow-lg w-full max-w-sm p-4 flex flex-col items-center"
      >
        <img
          src={profileData.image || logo}
          alt="Profile"
          className="w-24 h-24 rounded-full border-2 border-gray-300 p-1 mb-4"
        />

        <h2 className="text-gray-800 font-bold text-2xl mb-2 text-center">{authState.user.name}</h2>
        <p className="text-sm text-gray-600 text-center">ID#: {authState.user.think5whyscode}</p>
        <p className="text-sm text-gray-600 text-center">{authState.user.remarks}</p>
        <p className="text-sm text-gray-600 text-center">Email: {authState.user.email}</p>
        <p className="text-sm text-gray-600 text-center">Mobile: {authState.user.mobile}</p>
        <p className="text-sm text-gray-600 text-center mb-4">Place: {authState.user.city}</p>
        <p className="text-sm text-gray-600 text-center mb-4">Country: {authState.user.country}</p>

        <div className="flex justify-around mb-4">
          <a href={profileData.socialLinks?.facebook} target="_blank" rel="noopener noreferrer">
            <FontAwesomeIcon className="text-blue-600" icon={faFacebook} size="lg" />
          </a>
          <a href={profileData.socialLinks?.instagram} target="_blank" rel="noopener noreferrer">
            <FontAwesomeIcon className="text-pink-600" icon={faInstagram} size="lg" />
          </a>
          <a href={profileData.socialLinks?.twitter} target="_blank" rel="noopener noreferrer">
            <FontAwesomeIcon className="text-blue-400" icon={faTwitter} size="lg" />
          </a>
          <a href={profileData.personalPageLink} target="_blank" rel="noopener noreferrer">
            <FontAwesomeIcon className="text-blue-700" icon={faLinkedin} size="lg" />
          </a>
          <a href={profileData.personalPageLink} target="_blank" rel="noopener noreferrer">
            <FontAwesomeIcon className="text-gray-600" icon={faBriefcaseClock} size="lg" />
          </a>
        </div>

        <div className="bg-white p-2 rounded-md shadow-md mb-4">
          <QRCode value={`${window.location.origin}/pub/talking/card/${profileData.think5whysCode}`} size={96} />
        </div>

        <p className="bg-gray-100 text-gray-700 text-center text-xs p-1 rounded-md mb-1">
          Powered by PurpleDoor@
          <img src={logo} alt="Think5Whys" className="inline-block w-4 h-4 mx-2" />
          think5whys.com
        </p>
      </div>
      <div className='flex flex-row justify-center w-full'>
        <button
          onClick={downloadCard}
          className="w-40 text-xs lg:text-sm bg-green-600 text-white px-2 py-1 rounded shadow flex items-center gap-2"
        >
          <FontAwesomeIcon icon={faDownload} />
          Download
        </button>
      </div>
      <div className="p-1 border-t border-blue-400"><p className='text-yellow-200 text-[10px]'>The Voice Is As Per Your Browser. This can be treated as fun!</p> </div>
      <div className="flex flex-row items-center hide-for-download">
          <select
            className="bg-white text-gray-700 px-2 py-1 rounded border w-40 mt-1 text-xs"
            value={ttsLang}
            onChange={(e) => setTtsLang(e.target.value)}
          >
            <option value="en-IN">English</option>
          </select>
          <button
              onClick={isSpeaking ? stopSpeaking : speakCard}
              className={`text-xs lg:text-sm w-40 px-2 py-1 rounded shadow flex items-center gap-2 ${
                  isSpeaking ? "bg-red-600" : "bg-blue-600"
              } text-white`}
          >
              <FontAwesomeIcon icon={isSpeaking ? faStop : faVolumeHigh} />
              {isSpeaking ? "Stop" : "Listen"}
          </button>
        </div>
    </div>
  );
};

export default PDTalkingCard;
